import Vue from 'vue';
import { apolloProvider } from '@/graphql/vueApollo'; // GraphQL connection
import EventBus from '@/utilities/eventBus';
import formatGraphQlError from '@/utilities/formatGraphQlError';
import { FORBIDDEN } from '@/utilities/errorCodes';
import logger from 'client-logger';

// Queries
import GET_PAYMENT_METHODS_BY_STORE_ID from '@/graphql/queries/getPaymentMethodsByStoreId.gql';

// Mutations
import CREATE_PAYMENT_METHOD from '@/graphql/mutations/createPaymentMethod.gql';
import SET_PAYMENT_METHOD_AS_DEFAULT from '@/graphql/mutations/setPaymentMethodAsDefault.gql';
import DELETE_PAYMENT_METHOD_BY_ID from '@/graphql/mutations/deletePaymentMethodById.gql';

// Error Handling
import AppState from '@/store/AppState';

const apollo = apolloProvider.defaultClient;

// An interface for getting, querying, and mutating our data. All methods with GraphQL queries by default return cached values, but can be forced to re-fetch.
const Payment = new Vue({
	data() {
		return {
			wallets: {}
		};
	},
	computed: {
		// Gets a list of all of the payment methods tied to the given store ID
		paymentMethods() {
			return storeId => {
				if (this.wallets[storeId] === undefined) {
					apollo
						.query({
							query: GET_PAYMENT_METHODS_BY_STORE_ID,
							variables: { storeId },
							fetchPolicy: 'network-only'
						})
						.then(response => {
							// Update our payment method data
							const results = response.data.getPaymentMethodsByStoreId;
							results.forEach(paymentMethod => this.setPaymentMethod(storeId, paymentMethod));
						})
						.catch(e => {
							const formattedError = formatGraphQlError(e);

							if (formattedError) {
								logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
									{ error: formattedError, storeId },
									'Failed to get payment method by store ID'
								);
							} else {
								logger.error({ error: e, storeId }, 'Failed to get payment method by ID');
							}

							// Set our payment method as undefined so that we know there was an error
							this.setPaymentMethod(storeId, null);
						});
				}
				return this.wallets[storeId];
			};
		}
	},
	methods: {
		// Set the payment method's details
		setPaymentMethod(storeId, paymentMethod) {
			if (this.wallets[storeId] === undefined) {
				this.$set(this.wallets, storeId, []);
			}

			if (paymentMethod) {
				const existingMethod = this.wallets[storeId].find(existingPaymentMethod => existingPaymentMethod.id === paymentMethod.id);
				if (existingMethod) {
					Object.assign(existingMethod, paymentMethod);
				} else {
					this.wallets[storeId].push(paymentMethod);
					this.$set(this.wallets, storeId, this.wallets[storeId]);
				}
			} else {
				this.wallets[storeId] = undefined;
			}
		},
		removePaymentMethod(storeId, id) {
			// Grab the wallet
			const wallet = [...this.wallets[storeId]];

			if (wallet) {
				const methodToDelete = wallet.find(method => method.id === id);

				// Delete it if found
				if (methodToDelete) {
					const index = wallet.indexOf(methodToDelete);
					wallet.splice(index, 1);
					this.$set(this.wallets, storeId, wallet);
				}
			}
		},

		// Creates a new payment method tied to the indicated store
		async createPaymentMethod(storeId, token, setDefault = false) {
			try {
				const {
					data: { createPaymentMethod: payMethod }
				} = await apollo.mutate({
					mutation: CREATE_PAYMENT_METHOD,
					variables: {
						storeId,
						token,
						setDefault
					}
				});

				this.setPaymentMethod(storeId, payMethod);

				// Tell Vue about our
				if (setDefault) {
					this.updateDefaultPaymentMethod(storeId, payMethod);
				}

				return { data: payMethod };
			} catch (e) {
				const formattedError = AppState.showGeneralError(e);
				logger.error({ storeId, error: (formattedError && formattedError.error) || e }, 'Error trying to create payment method for store.');

				return formattedError;
			}
		},

		// Sets the desired method as the default payment method
		async setPaymentMethodAsDefault(storeId, paymentMethod) {
			try {
				const {
					data: { setPaymentMethodAsDefault: result }
				} = await apollo.mutate({
					mutation: SET_PAYMENT_METHOD_AS_DEFAULT,
					variables: {
						storeId,
						walletId: paymentMethod.walletId,
						methodId: paymentMethod.id
					}
				});

				this.updateDefaultPaymentMethod(storeId, paymentMethod);

				return { data: result };
			} catch (e) {
				const formattedError = AppState.showGeneralError(e);
				logger.error(
					{ storeId, paymentMethodId: paymentMethod.id, error: (formattedError && formattedError.error) || e },
					'Error trying to set payment method as default:'
				);

				return formattedError;
			}
		},

		// Deletes a payment method from the indicated store
		async deletePaymentMethod(storeId, paymentMethod) {
			try {
				const response = await apollo.mutate({
					mutation: DELETE_PAYMENT_METHOD_BY_ID,
					variables: {
						storeId,
						walletId: paymentMethod.walletId,
						methodId: paymentMethod.id
					},
					fetchPolicy: 'no-cache'
				});

				this.removePaymentMethod(storeId, paymentMethod.id);

				return { data: response.data.deletePaymentMethodById };
			} catch (e) {
				const formattedError = AppState.showGeneralError(e);
				logger.error(
					{ storeId, paymentMethodId: paymentMethod.id, error: (formattedError && formattedError.error) || e },
					`Error trying to delete payment method.`
				);
				return formattedError;
			}
		},
		updateDefaultPaymentMethod(storeId, paymentMethod) {
			const defaultFalse = { isDefault: false };
			const currentDefault = this.wallets[storeId].find(method => method.isDefault);

			paymentMethod.isDefault = true;

			if (currentDefault && currentDefault.id !== paymentMethod.id) {
				// Deselect the current default.
				Object.assign(currentDefault, defaultFalse);
			}

			this.setPaymentMethod(storeId, paymentMethod);
		},

		// A method that will accept incoming subscription payloads and do with them what needs to be done
		subscriptionHandler(payload) {
			let paymentMethod = payload.paymentMethod;
			let storeId = payload.storeId;

			// If the paymentMethod is the default payment method, deselect the current default
			if (paymentMethod.isDefault) {
				this.updateDefaultPaymentMethod(storeId, paymentMethod);
			}

			// We could have very different actions to execute depending on the scenario
			switch (payload.action) {
				case 'createPaymentMethod':
				case 'updatePaymentMethod':
					this.setPaymentMethod(storeId, paymentMethod);
					break;
				case 'delete':
					this.removePaymentMethod(storeId, paymentMethod.id);
					break;
			}
		},

		// Wipes everything out that we have available directly through our props
		clearAll() {
			this.$set(this, 'wallets', {});
		}
	},
	created() {
		// Now to hook into real-time data updates
		EventBus.$on('payment', this.subscriptionHandler);
		EventBus.$on('payments', this.subscriptionHandler);
	}
});

export default Payment;
